import React from 'react'
import { useMutation } from '@tanstack/react-query'
import { Modal } from '../../../common/Modal'
import { ModalHeader } from '../../../common/Modal/ModalHeader'
import { ModalBody } from '../../../common/Modal/ModalBody'
import { theme } from '../../../../theme/theme'
import { Button, ButtonVariant } from '../../../common/Button'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { PreApprovedListItem } from './PreApprovedListItem'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../common/Toast/toast-type'
import { useModal } from '../../../../hooks/useModal'
import { ConfirmationModal } from '../../../common/ConfirmationModal'
import { inviteAllPreApprovedUsers } from '../../../../api/societies'
import { IPreApprovedList } from '../../../../interfaces/models/societies.interfaces'

interface PreApprovedModalProps {
  show: boolean
  close: () => void
  preApproved: IPreApprovedList[]
  children?: React.ReactNode
}

export const PreApprovedModal = ({
  children,
  show,
  close,
  preApproved,
}: PreApprovedModalProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { society } = useCurrentSociety()
  const { setToastNotification } = useToastNotifications()
  const {
    show: showRemindAllConfirmationModal,
    open: openRemindAllConfirmationModal,
    close: closeRemindAllConfirmationModal,
  } = useModal()

  const { mutate: mutateInviteAllPreApprovedUsers, isPending } = useMutation({
    mutationFn: inviteAllPreApprovedUsers,
    onSuccess: () => {
      close()
      setToastNotification(
        ToastType.SUCCESS,
        translate('preApproved.flashMessage.remindAllSuccess')
      )
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('preApproved.flashMessage.remindAllFailure')
      )
    },
  })
  const sendAllUsersReminder = async (): Promise<void> => {
    society && mutateInviteAllPreApprovedUsers(society._id)
  }

  const invitingAllPreApproved = isPending
  return (
    <>
      {children}
      <Modal show={show}>
        {{
          header: (
            <ModalHeader onClose={close}>
              <div>
                <p style={theme.textVariants.h3}>
                  {translate('preApproved.title')}
                </p>

                <p style={theme.textVariants.base}>
                  {translate('preApproved.subtitle')}
                </p>
              </div>
            </ModalHeader>
          ),
          body: (
            <ModalBody>
              <div>
                {preApproved.map((_preApproved, index) => {
                  return (
                    <PreApprovedListItem
                      key={_preApproved._id}
                      preApproved={_preApproved}
                      className={
                        index === preApproved.length - 1 ? 'border-b' : ''
                      }
                      disableButtons={invitingAllPreApproved}
                    />
                  )
                })}
              </div>
              <div className="mt-2 self-end">
                <Button
                  label={translate('common.actions.cancel')}
                  variant={ButtonVariant.TEXT}
                  onClick={() => close()}
                  className="mr-4"
                />
                <Button
                  label={translate('preApproved.buttons.remindAllTitle')}
                  variant={ButtonVariant.PRIMARY}
                  onClick={openRemindAllConfirmationModal}
                  disabled={invitingAllPreApproved}
                  loading={invitingAllPreApproved}
                />
              </div>
            </ModalBody>
          ),
        }}
      </Modal>
      <ConfirmationModal
        title={translate('preApproved.confirmationDialog.remindAllTitle')}
        description={translate(
          'preApproved.confirmationDialog.remindAllDescription'
        )}
        show={showRemindAllConfirmationModal}
        close={closeRemindAllConfirmationModal}
        onConfirm={sendAllUsersReminder}
        confirmationButtonLabel={translate('common.actions.send')}
      />
    </>
  )
}
